'use client'

import { Command } from 'cmdk'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next-nprogress-bar'
import { useParams } from 'next/navigation'
import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { CommandPaletteEntry, NavCatalogGroup, getCommandPaletteEntries } from 'src/utils/cmsNavigationCatalog'
import { executeFunctionOnHotKey } from 'src/utils/hotkey-utils'

import { BrandGroup, WorkspaceGroup } from '@models/types'

import { Icon } from '../../basicUI/Icon'
import { Text } from '../../basicUI/Text'
import { Tooltip } from '../../basicUI/Tooltip'
import { css, keyframes, styled } from '../../stitches.config'

const GROUP_ORDER: NavCatalogGroup[] = ['publication', 'pages', 'newsletters', 'settings', 'workspace', 'actions']

const GROUP_LABEL_KEYS: Record<NavCatalogGroup, string> = {
  publication: 'groupPublication',
  pages: 'groupPages',
  newsletters: 'groupNewsletters',
  settings: 'groupSettings',
  workspace: 'groupWorkspace',
  actions: 'groupActions',
}

/**
 * Stricter than cmdk's default command-score fuzzy match, which can match
 * "email" across unrelated aliases like "templates" + "article" (e-m-a-i-l).
 * Prefer exact / prefix / whole-word containment on the label and each keyword.
 */
const scoreCommandMatch = (value: string, search: string, keywords: string[] = []): number => {
  const query = search.trim().toLowerCase()
  if (!query) return 1

  const haystacks = [value, ...keywords]
    .map(part => part.trim().toLowerCase())
    .filter(Boolean)

  let best = 0
  for (const haystack of haystacks) {
    if (haystack === query) {
      best = Math.max(best, 1)
      continue
    }
    if (haystack.startsWith(query)) {
      best = Math.max(best, 0.9)
      continue
    }
    if (haystack.includes(query)) {
      best = Math.max(best, 0.75)
      continue
    }

    // Multi-word queries: every query token must be a prefix of some haystack word.
    const queryTokens = query.split(/\s+/).filter(Boolean)
    const haystackWords = haystack.split(/[\s/_-]+/).filter(Boolean)
    if (
      queryTokens.length > 1 &&
      queryTokens.every(token => haystackWords.some(word => word.startsWith(token) || word.includes(token)))
    ) {
      best = Math.max(best, 0.6)
    }
  }

  return best
}

type CommandPaletteContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null)

export type CommandPaletteProviderProps = {
  workspacePermissions?: WorkspaceGroup[]
  brandPermissions?: BrandGroup[]
  newsletterProvider?: string | null
  children?: ReactNode
}

/**
 * Owns Cmd/Ctrl+K state, the dialog, and provides `CommandPaletteTrigger` via context.
 * Mount once per authenticated shell.
 */
export const CommandPaletteProvider: FC<CommandPaletteProviderProps> = ({
  workspacePermissions,
  brandPermissions,
  newsletterProvider,
  children,
}) => {
  const t = useTranslations('PLCommandPalette')
  const tMain = useTranslations('PLMainMenu')
  const tSettings = useTranslations('PLSettingsMenu')
  const router = useRouter()
  const params = useParams()
  const workspace = params?.workspace as string | undefined
  const brand = params?.brand as string | undefined
  const siteId = params?.siteId as string | undefined

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const entries = useMemo(
    () =>
      getCommandPaletteEntries({
        workspace,
        brand,
        siteId,
        workspacePermissions,
        brandPermissions,
        newsletterProvider,
      }),
    [workspace, brand, siteId, workspacePermissions, brandPermissions, newsletterProvider],
  )

  const resolveLabel = useCallback(
    (entry: CommandPaletteEntry): string => {
      if (entry.labelNamespace === 'PLMainMenu') return tMain(entry.labelKey)
      if (entry.labelNamespace === 'PLSettingsMenu') return tSettings(entry.labelKey)
      return t(entry.labelKey)
    },
    [t, tMain, tSettings],
  )

  const groupedEntries = useMemo(() => {
    const groups = new Map<NavCatalogGroup, Array<CommandPaletteEntry & { label: string }>>()
    for (const entry of entries) {
      const withLabel = { ...entry, label: resolveLabel(entry) }
      const list = groups.get(entry.group) || []
      list.push(withLabel)
      groups.set(entry.group, list)
    }
    return GROUP_ORDER.filter(group => (groups.get(group)?.length || 0) > 0).map(group => ({
      group,
      heading: t(GROUP_LABEL_KEYS[group]),
      items: groups.get(group) || [],
    }))
  }, [entries, resolveLabel, t])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setSearch('')
  }, [])

  const handleSelect = useCallback(
    (href: string) => {
      handleOpenChange(false)
      router.push(href)
    },
    [handleOpenChange, router],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      executeFunctionOnHotKey(
        'mod+k',
        () => {
          setOpen(current => {
            const next = !current
            if (!next) setSearch('')
            return next
          })
        },
        event,
      )
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const shortcutLabel = useMemo(() => {
    if (typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return t('shortcutMac')
    }
    return t('shortcutOther')
  }, [t])

  const contextValue = useMemo(() => ({ open, setOpen: handleOpenChange }), [open, handleOpenChange])

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
      <StyledCommandDialog
        open={open}
        onOpenChange={handleOpenChange}
        label={t('dialogLabel')}
        loop
        filter={scoreCommandMatch}
        overlayClassName={overlayClass()}
        contentClassName={contentClass()}>
        <InputRow>
          <Icon name="search" css={{ color: '$gs8' }} />
          <Command.Input
            value={search}
            onValueChange={setSearch}
            placeholder={t('placeholder')}
            data-testid="command-palette-input"
          />
          <KbdHint aria-hidden>{shortcutLabel}</KbdHint>
        </InputRow>
        <Command.List data-testid="command-palette-list">
          <Command.Empty>
            <EmptyState>
              <Text variant="body2" css={{ color: '$gs8' }}>
                {t('empty')}
              </Text>
            </EmptyState>
          </Command.Empty>
          {groupedEntries.map(({ group, heading, items }) => (
            <Command.Group key={group} heading={heading}>
              {items.map(item => (
                <Command.Item
                  key={item.id}
                  // Stable unique value; searchable text lives in keywords so the
                  // custom filter can match label/aliases without cmdk's sparse fuzzy score.
                  value={item.id}
                  keywords={[item.label, ...item.keywords]}
                  onSelect={() => handleSelect(item.href)}
                  data-testid={`command-palette-item-${item.id}`}>
                  {item.iconName ? (
                    <Icon name={item.iconName} css={{ color: '$gs9', flexShrink: 0 }} />
                  ) : (
                    <Icon name="chevron_right" css={{ color: '$gs6', flexShrink: 0 }} />
                  )}
                  <Text variant="body2" css={{ color: '$gs12', flex: 1, minWidth: 0 }} oll={true}>
                    {item.label}
                  </Text>
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </StyledCommandDialog>
    </CommandPaletteContext.Provider>
  )
}

/** Top-bar search button. Must be rendered under `CommandPaletteProvider`. */
export const CommandPaletteTrigger: FC = () => {
  const t = useTranslations('PLCommandPalette')
  const ctx = useContext(CommandPaletteContext)
  const shortcutLabel = useMemo(() => {
    if (typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return t('shortcutMac')
    }
    return t('shortcutOther')
  }, [t])

  if (!ctx) return null

  return (
    <Tooltip content={t('triggerTooltip', { shortcut: shortcutLabel })} useSpanElementForTrigger={true}>
      <TriggerButton
        type="button"
        aria-label={t('triggerAriaLabel', { shortcut: shortcutLabel })}
        data-testid="command-palette-trigger"
        onClick={() => ctx.setOpen(true)}>
        <Icon name="search" pointer={true} />
        <ShortcutBadge>{shortcutLabel}</ShortcutBadge>
      </TriggerButton>
    </Tooltip>
  )
}

/**
 * Convenience wrapper: provider + optional top-bar trigger.
 * Prefer `CommandPaletteProvider` + `CommandPaletteTrigger` when the trigger
 * must sit inside an existing top-bar layout.
 */
export const CommandPalette: FC<
  CommandPaletteProviderProps & {
    showTrigger?: boolean
  }
> = ({ showTrigger = true, children, ...providerProps }) => {
  return (
    <CommandPaletteProvider {...providerProps}>
      {showTrigger && <CommandPaletteTrigger />}
      {children}
    </CommandPaletteProvider>
  )
}

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
})

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -8px) scale(.98)' },
  '100%': { opacity: 1, transform: 'translate(-50%, 0) scale(1)' },
})

// Above PageTopBar (z-index 2000) and Modal (1101).
const OVERLAY_Z = 2100
const CONTENT_Z = 2101

const overlayClass = css({
  position: 'fixed',
  inset: 0,
  backgroundColor: '$b5',
  zIndex: OVERLAY_Z,
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${overlayShow.toString()} $fast cubic-bezier(0.16, 1, 0.3, 1)`,
  },
})

const contentClass = css({
  position: 'fixed',
  top: '20%',
  left: '50%',
  transform: 'translate(-50%, 0)',
  width: 'min(640px, calc(100vw - 32px))',
  maxHeight: 'min(480px, 70vh)',
  backgroundColor: '$gs1',
  borderRadius: '$4',
  boxShadow: '$bottom',
  border: '1px solid $gs4',
  zIndex: CONTENT_Z,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${contentShow.toString()} $fast cubic-bezier(0.16, 1, 0.3, 1)`,
  },
})

const StyledCommandDialog = styled(Command.Dialog, {
  '& [cmdk-input]': {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: '$gs12',
    fontSize: '16px',
    lineHeight: '24px',
    width: '100%',
    '&::placeholder': {
      color: '$gs7',
    },
  },
  '& [cmdk-list]': {
    maxHeight: '360px',
    overflowY: 'auto',
    padding: '$2',
    overscrollBehavior: 'contain',
  },
  '& [cmdk-group-heading]': {
    px: '$3',
    py: '$2',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 600,
    color: '$gs8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  '& [cmdk-item]': {
    display: 'flex',
    alignItems: 'center',
    gap: '$3',
    px: '$3',
    py: '$2',
    borderRadius: '$3',
    cursor: 'pointer',
    userSelect: 'none',
    '&[data-selected="true"]': {
      backgroundColor: '$gs3',
    },
    '&[aria-disabled="true"]': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  '& [cmdk-empty]': {
    py: '$6',
  },
})

const InputRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  px: '$4',
  py: '$3',
  borderBottom: '1px solid $gs4',
})

const EmptyState = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  px: '$4',
})

const KbdHint = styled('span', {
  flexShrink: 0,
  fontSize: '12px',
  lineHeight: '16px',
  color: '$gs8',
  backgroundColor: '$gs3',
  border: '1px solid $gs5',
  borderRadius: '$2',
  px: '$2',
  py: '$1',
})

const TriggerButton = styled('button', {
  display: 'flex',
  flexFlow: 'row nowrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$2',
  padding: '$2',
  borderRadius: '$3',
  border: 'none',
  backgroundColor: '$gs2',
  cursor: 'pointer',
  color: '$gs12',
  transition: 'background-color $normal ease-in-out',
  '&:hover': {
    backgroundColor: '$gs3',
  },
})

const ShortcutBadge = styled('span', {
  display: 'none',
  fontSize: '11px',
  lineHeight: '14px',
  color: '$gs8',
  backgroundColor: '$gs1',
  border: '1px solid $gs5',
  borderRadius: '$2',
  px: '$2',
  py: '$1',
  '@bp2': {
    display: 'inline-flex',
  },
})
